import { Box, ButtonBase, Collapse, styled, SxProps, Theme } from '@mui/material';
import { motion } from 'framer-motion';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { READ_ROW_DIM_OPACITY } from './inbox-pane.constants';

// the swap is CSS, not a JS `isHovered` flag: that flag sticks when the open menu's
// backdrop swallows the row's mouseleave
export const ROW_META_CLASS = 'InboxPaneRowMeta';
export const ROW_MENU_CLASS = 'InboxPaneRowMenu';

// the read-state dim targets this cell only, so the avatar and the status/timestamp column
// keep their full-strength colour
export const ROW_TEXT_CLASS = 'InboxPaneRowText';

export const Header = styled(Box, { name: 'InboxPaneHeader' })(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '0.25rem',
  flexShrink: 0,
  padding: '0.125rem 0.25rem 0.5rem',
  margin: '0 0 0.375rem',
  borderBottom: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
}));

// mirrors the mockup's `.head-tool` — the generic icon button used for the
// pane's Filter/More triggers
export const getHeadToolSx = (theme: Theme): SxProps<Theme> => ({
  width: '1.625rem',
  height: '1.625rem',
  borderRadius: '0.375rem',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  transition: 'background-color .12s, color .12s',

  '&:hover': {
    backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHighest,
    color: theme.palette.custom.OV.sys.onBackground,
  },
});

export const ChipsRow = styled(Box, { name: 'InboxPaneChipsRow' })({
  flexShrink: 0,
  padding: '0 0.25rem 0.5rem',
});

export const ScrollArea = styled(Box, { name: 'InboxPaneScrollArea' })({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  paddingBottom: '0.75rem',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  // touch opts out globally (index.css); this covers mouse drag-select on desktop
  userSelect: 'none',
});

// the radius matches ItemRow's so the separator's corners curve with the row instead of
// running edge-to-edge across the pane
export const ItemTransitionWrapper = styled(Collapse, {
  name: 'InboxPaneItemTransitionWrapper',
})(({ theme }) => ({
  flexShrink: 0,
  overflow: 'hidden',
  borderRadius: '0.5rem',

  '& + &': {
    borderTop: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
  },
}));

export const ItemRow = styled(ButtonBase, {
  name: 'InboxPaneItemRow',
  shouldForwardProp: (prop) =>
    prop !== 'isSelected' && prop !== 'isActive' && prop !== 'isRead',
})<{ isSelected?: boolean; isActive?: boolean; isRead?: boolean }>(({
  theme,
  isSelected,
  isActive,
  isRead,
}) => {
  let backgroundColor = 'transparent';
  if (isSelected) backgroundColor = theme.palette.custom.OV.sys.surfaceContainerHigh;
  else if (isActive) backgroundColor = theme.palette.action.hover;

  return {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '2.25rem 1fr auto',
    gap: '0.625rem',
    alignItems: 'center',
    padding: '0.5rem 0.375rem',
    borderRadius: '0.5rem',
    textAlign: 'left',
    fontFamily: 'inherit',
    position: 'relative',
    backgroundColor,

    WebkitTapHighlightColor: 'transparent',

    // only the subject and snippet fade — dimming the row itself would take its background
    // and the status glyph with it. The open row and the one under the cursor come back to
    // full strength, so the row you are actually reading is never the faint one
    ...(isRead && !isSelected
      ? {
          [`& .${ROW_TEXT_CLASS}`]: {
            opacity: READ_ROW_DIM_OPACITY,
            transition: 'opacity .12s',
          },
          [`&:hover .${ROW_TEXT_CLASS}`]: {
            opacity: 1,
          },
        }
      : {}),

    '&:hover': {
      backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHighest,
    },

    // hover-capable pointers only: on touch a tap sets :hover and it sticks until the next
    // tap elsewhere, which would blank the right column with no menu rendered to replace it
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        [`& .${ROW_META_CLASS}`]: {
          visibility: 'hidden',
        },
        [`& .${ROW_MENU_CLASS}`]: {
          visibility: 'visible',
        },
      },
    },
  };
});

export const SwipeContainer = styled(Box, { name: 'InboxPaneSwipeContainer' })({
  position: 'relative',
  overflow: 'hidden',
  touchAction: 'pan-y',
});

export const MotionSwipeable = styled(motion.div, { name: 'InboxPaneMotionSwipeable' })({
  width: '100%',
});

export const ArchiveRevealBackground = styled(motion.div, {
  name: 'InboxPaneArchiveReveal',
})(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  paddingRight: '1.5rem',
  borderRadius: '0.5rem',
  backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHighest,
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  pointerEvents: 'none',
}));

export const AvatarCell = styled(Box, { name: 'InboxPaneAvatarCell' })({});

export const Meta = styled(Box, { name: 'InboxPaneMeta' })({
  minWidth: 0,
});

export const SubjectLine = styled(Box, {
  name: 'InboxPaneSubjectLine',
  shouldForwardProp: (prop) => prop !== 'isUnread',
})<{ isUnread?: boolean }>(({ theme, isUnread }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.375rem',
  fontSize: '0.78125rem',
  fontWeight: isUnread ? 600 : 500,
  color: isUnread
    ? theme.palette.custom.OV.sys.onBackground
    : theme.palette.custom.OV.sys.onSurface,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
}));

export const UnreadDot = styled(Box, { name: 'InboxPaneUnreadDot' })(({ theme }) => ({
  width: '0.4375rem',
  height: '0.4375rem',
  borderRadius: '50%',
  flexShrink: 0,
  backgroundColor: theme.palette.custom.OV.sys.secondary,
}));

export const ConversationId = styled('span', { name: 'InboxPaneConversationId' })(
  ({ theme }) => ({
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
    fontWeight: 500,
    fontVariantNumeric: 'tabular-nums',
    flexShrink: 0,
  }),
);

export const SubjectText = styled('span', { name: 'InboxPaneSubjectText' })({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const Snippet = styled(Box, { name: 'InboxPaneSnippet' })(({ theme }) => ({
  fontSize: '0.6875rem',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  lineHeight: 1.4,
  marginTop: '0.125rem',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

// meta and the menu share one grid cell and only flip `visibility`, so both stay laid out
// and the row height never changes on hover
export const RightCol = styled(Box, { name: 'InboxPaneRightCol' })(({ theme }) => ({
  display: 'grid',
  gridTemplateAreas: '"rowActions"',
  alignItems: 'center',
  justifyItems: 'center',
  fontSize: '0.65625rem',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  fontVariantNumeric: 'tabular-nums',
  flexShrink: 0,
}));

// `isMenuOpen` holds the swap while the dropdown is open, since its backdrop covers the
// row and `:hover` no longer matches
export const RowMeta = styled(Box, {
  name: 'InboxPaneRowMeta',
  shouldForwardProp: (prop) => prop !== 'isMenuOpen',
})<{ isMenuOpen?: boolean }>(({ isMenuOpen }) => ({
  gridArea: 'rowActions',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.125rem',
  visibility: isMenuOpen ? 'hidden' : 'visible',
}));

export const RowMenuSlot = styled(Box, {
  name: 'InboxPaneRowMenuSlot',
  shouldForwardProp: (prop) => prop !== 'isMenuOpen',
})<{ isMenuOpen?: boolean }>(({ theme, isMenuOpen }) => ({
  gridArea: 'rowActions',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '0.375rem',
  visibility: isMenuOpen ? 'visible' : 'hidden',
  backgroundColor: isMenuOpen
    ? theme.palette.custom.OV.sys.surfaceContainerHighest
    : 'transparent',
  color: isMenuOpen
    ? theme.palette.custom.OV.sys.onSurface
    : theme.palette.custom.OV.sys.onSurfaceVariant,
}));

export const SkeletonRow = styled(Box, { name: 'InboxPaneSkeletonRow' })({
  display: 'grid',
  gridTemplateColumns: '2.25rem 1fr auto',
  gap: '0.625rem',
  alignItems: 'flex-start',
  padding: '0.5rem 0.375rem',
});

export const EmptyState = styled(Box, { name: 'InboxPaneEmptyState' })({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '3rem 0.75rem',
  textAlign: 'center',
});

export const EmptyIcon = styled(DynamicIcon, { name: 'InboxPaneEmptyIcon' })(({ theme }) => ({
  width: '3rem',
  height: '3rem',
  fontSize: '3rem',
  color: theme.palette.custom.OV.sys.outlineVariant,
}));
